import { Component, EventEmitter, Injector, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { ApiService } from '@api/api.service';
import { ModalService } from '@shared/modal/modal.service';
import { DownloadService } from '@shared/services/download.service';
import { HistoryGetParams } from '@shared/types/history_get_params';
import { TableConfig } from '@shared/types/table';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { VersionDatasource } from './version.datasource';

@Component({
  selector: 'rav-version',
  templateUrl: './version.component.html',
  styleUrls: ['./version.component.scss']
})
export class VersionComponent implements OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @Input() currentVersion: string;
  @Input() id: string;
  @Output() versionEvent = new EventEmitter<any>();
  modalTitle: string;
  documentId: string;
  tableConfig: TableConfig = {
    columns: [
      { key: 'view_version', header: 'VERSION', sortBy: 'version' },
      { key: 'updated_at', header: 'DATE' },
      { key: 'user', header: 'USER' },
      { key: 'last_action', header: 'LAST ACTION' },
      { key: 'action', header: 'ACTION' }
    ],
    actions: [],
    isSearchable: false,
    fixedPageSize: 6
  };
  dataSource: VersionDatasource;
  paginatorLength = new BehaviorSubject<number>(0);

  paginatorLength$ = this.paginatorLength.asObservable();
  private subscriptions: Subscription[] = [];

  private get params(): HistoryGetParams {
    const params: HistoryGetParams = { page: this.paginator.pageIndex + 1, per_page: this.paginator.pageSize };
    if (this.sort.direction === 'asc') params.asc = this.sort.direction;
    if (this.sort.active) params.order_by = this.getSortParam(this.sort.active);
    return params;
  }

  constructor(
    private modal: ModalService,
    private downloadService: DownloadService,
    private injector: Injector,
    private api: ApiService,
    private snackbar: MatSnackBar
  ) {}

  /**
   * Open the version modal
   */
  open(document) {
    this.resetSort();
    this.modalTitle = `Version History: ${document.title}`;
    this.documentId = document.id;
    this.modal.open.next({ modalId: 'version' });
    this.dataSource = new VersionDatasource(this.injector, document.id, document.version);
    this.dataSource.loadData(this.params);
    this.subscriptions.push(
      this.dataSource.paginatedGet$.subscribe((paginatedGet) => {
        this.paginatorLength.next(paginatedGet.total);
      })
    );
    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    merge(this.sort.sortChange, this.paginator.page)
      // reload table after sort or page change
      .pipe(tap(() => this.dataSource.loadData(this.params)))
      .subscribe();
  }

  /**
   * Determines what columns to display by the key names.
   *
   * @returns columns to be displayed
   */
  get columnsToDisplay(): string[] {
    const columns = this.tableConfig.columns.map((column) => column.key);
    return columns;
  }

  /**
   * Download a specific version of document.
   *
   * @param document Document to download
   */
  download(document) {
    document.id = document.document_id;
    this.downloadService.downloadVersion(document, document.view_version);
  }

  /**
   * Revert version of the document.
   *
   * @param document Document to revert
   */
  async restore(document) {
    let restoreMessage = 'Document version restored successfully!';
    let isSuccess = true;
    try {
      const newDocument = await this.api.documents.byId.byVersion
        .patch(this.documentId, document.view_version)
        .toPromise();
      this.versionEvent.emit(newDocument);
      this.modal.close.next();
    } catch (error) {
      console.error(error);
      restoreMessage = 'Document version restored failed!';
      isSuccess = false;
    } finally {
      this.snackbar.open(restoreMessage, '', {
        duration: 5000,
        panelClass: [isSuccess ? 'success-bg' : 'alert-bg', 'bold', 'light-color']
      });
    }
  }

  /**
   * Takes the column to be used to sort the table by and checks if the attribute
   * to pass as a parameter to the api should be the column attribute name or a different one specified in the config
   *
   * @param colName the name of the column to sort the table by.
   * @returns the attribute name to use a sorting parameter.
   */
  getSortParam(colName: string): string {
    const column = this.tableConfig.columns.find((col) => col.key === colName);
    if (column) return column.sortBy ? column.sortBy : colName;
    return colName;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.resetSort();
  }

  /**
   * Reset sort to be version
   */
  private resetSort() {
    this.sort.active = 'version';
    this.sort.direction = 'desc';
    this.sort._stateChanges.next();
  }
}
